"use client";

import { Component, ReactNode, ErrorInfo } from "react";
import { logger } from "@/lib/infra/logger";
import { reportClientError } from "@/lib/errors/client";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const stack = errorInfo.componentStack?.slice(0, 4000);
    logger.error("ErrorBoundary caught:", error, { componentStack: stack });
    reportClientError(error, {
      tags: { source: "react-boundary" },
      extra: { componentStack: stack },
    });
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex min-h-[400px] items-center justify-center p-8">
            <div className="text-center">
              <h2 className="mb-4 text-2xl font-bold">Something went wrong</h2>
              <p className="text-muted-foreground mb-4">We&apos;re sorry for the inconvenience</p>
              <button
                onClick={() => this.setState({ hasError: false })}
                className="bg-foreground text-background px-4 py-2"
              >
                Try again
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
